// @ts-strict-ignore
import Money from "@dashboard/components/Money";
import { DashboardModal } from "@dashboard/components/Modal";
import { useUserPermissions } from "@dashboard/auth/hooks/useUserPermissions";
import { PermissionEnum, VendorDetailsQuery, useVendorDetailsQuery } from "@dashboard/graphql";
import { Box, Divider, Skeleton, Text } from "@saleor/macaw-ui-next";
import { Button } from "@saleor/macaw-ui";
import moment from "moment-timezone";
import React, { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getUserName } from "@dashboard/misc";

interface VendorDetailsDialogProps {
  open: boolean;
  vendorId: string | null;
  onClose: () => void;
}

const VendorDetailsDialog: React.FC<VendorDetailsDialogProps> = ({ open, vendorId, onClose }) => {
  const intl = useIntl();
  const permissions = useUserPermissions();

  const hasManageOrders = useMemo(
    () => permissions?.some(p => p.code === PermissionEnum.MANAGE_ORDERS) ?? false,
    [permissions],
  );

  const hasManageStaff = useMemo(
    () => permissions?.some(p => p.code === PermissionEnum.MANAGE_STAFF) ?? false,
    [permissions],
  );

  const { data, loading } = useVendorDetailsQuery({
    skip: !open || !vendorId,
    variables: {
      id: vendorId || "",
      PERMISSION_MANAGE_ORDERS: hasManageOrders,
      PERMISSION_MANAGE_STAFF: hasManageStaff,
    },
  });

  const vendor: VendorDetailsQuery["user"] | null | undefined = data?.user;

  return (
    <DashboardModal open={open} onChange={onClose}>
      <DashboardModal.Content size="md">
        <DashboardModal.Header>
          <DashboardModal.Title>
            <FormattedMessage id="vendorDetailsDialogTitle" defaultMessage="Vendor Details" />
          </DashboardModal.Title>
        </DashboardModal.Header>

        <DashboardModal.Grid>
          {loading ? (
            <Skeleton __height="200px" />
          ) : !vendor ? (
            <Text>
              <FormattedMessage
                id="vendorDetailsDialogEmpty"
                defaultMessage="No vendor details available."
              />
            </Text>
          ) : (
            <Box display="flex" flexDirection="column" gap={6}>
              {/* Basic info */}
              <Box
                display="grid"
                gap={4}
                __gridTemplateColumns={["1fr", "repeat(2, minmax(0, 1fr))"]}
              >
                <Box>
                  <Text variant="bodyStrong">
                    <strong>
                      <FormattedMessage id="vendorDetailsName" defaultMessage="Name" />:
                    </strong>
                  </Text>
                  <Text marginLeft={1}>{getUserName(vendor) ?? "N/A"}</Text>
                </Box>
                <Box>
                  <Text variant="bodyStrong">
                    <strong>
                      <FormattedMessage id="vendorDetailsEmail" defaultMessage="Email" />:
                    </strong>
                  </Text>
                  <Text marginLeft={1}>{vendor.email || "N/A"}</Text>
                </Box>
                <Box>
                  <Text variant="bodyStrong">
                    <strong>
                      <FormattedMessage id="vendorDetailsJoined" defaultMessage="Joined" />:
                    </strong>
                  </Text>
                  <Text marginLeft={1}>
                    {vendor.dateJoined ? moment(vendor.dateJoined).format("DD MMM YYYY") : "N/A"}
                  </Text>
                </Box>
              </Box>

              <Divider />

              {/* Addresses */}
              {/* <Box
                display="grid"
                gap={4}
                __gridTemplateColumns={["1fr", "repeat(2, minmax(0, 1fr))"]}
              >
                <Box>
                  <Text variant="bodyStrong">
                    <strong>
                      <FormattedMessage
                        id="vendorDetailsDefaultShipping"
                        defaultMessage="Shipping Address"
                      />
                      :
                    </strong>
                  </Text>
                  <Text marginLeft={1}>
                    {vendor.defaultShippingAddress
                      ? [
                          vendor.defaultShippingAddress.firstName,
                          vendor.defaultShippingAddress.lastName,
                        ]
                          .filter(Boolean)
                          .join(" ") || "-"
                      : "N/A"}
                  </Text>
                  {vendor.defaultShippingAddress && (
                    <Text marginLeft={1}>
                      {vendor.defaultShippingAddress.streetAddress1}
                      {vendor.defaultShippingAddress.streetAddress2
                        ? `, ${vendor.defaultShippingAddress.streetAddress2}`
                        : ""}
                      , {vendor.defaultShippingAddress.city}{" "}
                      {vendor.defaultShippingAddress.postalCode},{" "}
                      {vendor.defaultShippingAddress.country.country}
                    </Text>
                  )}
                </Box>

                <Box>
                  <Text variant="bodyStrong">
                    <strong>
                      <FormattedMessage
                        id="vendorDetailsDefaultBilling"
                        defaultMessage="Billing Address"
                      />
                      :
                    </strong>
                  </Text>
                  <Text marginLeft={1}>
                    {vendor.defaultBillingAddress
                      ? [
                          vendor.defaultBillingAddress.firstName,
                          vendor.defaultBillingAddress.lastName,
                        ]
                          .filter(Boolean)
                          .join(" ") || "-"
                      : "N/A"}
                  </Text>
                  {vendor.defaultBillingAddress && (
                    <Text marginLeft={1}>
                      {vendor.defaultBillingAddress.streetAddress1}
                      {vendor.defaultBillingAddress.streetAddress2
                        ? `, ${vendor.defaultBillingAddress.streetAddress2}`
                        : ""}
                      , {vendor.defaultBillingAddress.city}{" "}
                      {vendor.defaultBillingAddress.postalCode},{" "}
                      {vendor.defaultBillingAddress.country.country}
                    </Text>
                  )}
                </Box>
              </Box>

              <Divider /> */}

              {/* Recent orders */}
              {hasManageOrders && vendor.orders && vendor.orders.edges.length > 0 && (
                <Box display="flex" flexDirection="column" gap={3}>
                  <Text variant="bodyStrong">
                    <FormattedMessage
                      id="vendorDetailsRecentOrders"
                      defaultMessage="Recent orders"
                    />
                  </Text>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {vendor.orders.edges.map(edge => {
                      const order = edge.node;
                      return (
                        <Box key={order.id} display="flex" justifyContent="space-between" gap={4}>
                          <Text>
                            {order.number} – {moment(order.created).format("DD MMM YYYY, HH:mm")}
                          </Text>
                          <Text>
                            {order.total?.gross ? <Money money={order.total.gross} /> : "-"}
                          </Text>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DashboardModal.Grid>
        <DashboardModal.Actions>
          <Button variant="secondary" onClick={onClose}>
            <FormattedMessage id="vendorDetailsClose" defaultMessage="Close" />
          </Button>
        </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

export default VendorDetailsDialog;
